import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Menu, message, notification } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import DetailPageHeader from '../detail_page_header/DetailPageHeader';
import {
	downloadWalkthroughCSV,
	downloadWalkthroughScoreCSV,
	generateWalkthroughPDF,
	getWalkthroughPDFStatus,
	walkthroughsRestCrudThunksForBuyer,
	walkthroughsRestCrudThunksForSupplier,
} from '../../thunks/walkthroughs_thunks';
import { DownOutlined } from '@ant-design/icons';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import queryString from 'querystring';
import { goBackLink } from '../../utils/HistoryUtils';
import DeleteConfirmButton from '../delete_pop_confirm/DeletePopConfirm';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import { WebSocketContext } from '../../context/WebSocketContext';
import { notify, WEB_SOCKET_MESSAGE_TYPES } from '../../utils/WebSocketUtils';
import { DEFAULT_NOTIFICATION_DURATION } from '../../utils/WorkOrderUtils';
import NewMessageNotification from '../work_orders_detail_page/NewMessageNotification';
import WalkthroughDetailListener from './WalkthroughDetailListener';
import { v4 as uuidv4 } from 'uuid';
import OWPoll from '../common/OWPoll';

require('./WalkthroughsDetailPage.less');

const WalkthroughsDetailPage: FC<any> = ({
	getWalkthrough,
	match,
	walkthroughsFetching,
	history,
	location,
	walkthrough,
	deleteWalkthrough,
	downloadWalkthroughCSV,
	downloadWalkthroughScoreCSV,
	fetchPdfStatus,
	generatePDF,
	childRoutes,
	companyConfig,
	userType,
}): React.ReactElement => {
	const [pdfGenerationInitiated, setPdfGenerationInitiated] = useState(false);
	const pdfLinkRef = useRef(null);
	const socketContext = useContext(WebSocketContext);

	const walkthroughId = useMemo(() => match.params.id, [match.params.id]);

	// Keep latest pathname in a ref so the listener (attached once) always reads current state
	const pathnameRef = useRef(location.pathname);
	useEffect(() => {
		pathnameRef.current = location.pathname;
	}, [location.pathname]);

	const handleMessage = useCallback(
		(event) => {
			try {
				const data = JSON.parse(event.data);

				if (pathnameRef.current.includes('notes')) return;
				if (data.walkthroughId != walkthroughId) return;

				switch (data.messageType) {
					case WEB_SOCKET_MESSAGE_TYPES.NEW_NOTE:
						const notificationKey = uuidv4();
						notify();
						notification.open({
							key: notificationKey,
							message: <NewMessageNotification data={data} />,
							placement: 'topRight',
							duration: DEFAULT_NOTIFICATION_DURATION,
							onClick: () => {
								notification.close(notificationKey);
								history.replace(
									`/${userType}/siteSurveys/walkthrough/detail/${walkthroughId}/notes`
								);
							},
						});
						break;
					default:
						break;
				}
			} catch (err) {
				console.log('JSON parse error', err);
			}
		},
		[walkthroughId, history, userType]
	);

	const downloadPDF = useCallback(() => {
		const pdfLink = nullSafeGet('pdfLink', walkthrough);
		if (pdfLink) {
			window.open(pdfLink, '_blank');
			return;
		}
		generatePDF(walkthroughId)
			.then((res) => {
				message.success(nullSafeGet('data', res) || 'PDF generation initiated.');
				setPdfGenerationInitiated(true);
			})
			.catch((err) => {
				message.error(err || 'Failed to generate PDF.');
			});
	}, [generatePDF, walkthroughId, walkthrough]);

	const onPdfPollComplete = useCallback(
		(res) => {
			const completedResult = res && res.find((r) => r && r.pdfLink);
			if (completedResult) {
				pdfLinkRef.current = completedResult.pdfLink;
			}
			getWalkthrough(walkthroughId);
		},
		[getWalkthrough, walkthroughId]
	);

	const onOpenPdf = useCallback(() => {
		const pdfLink = pdfLinkRef.current || nullSafeGet('pdfLink', walkthrough);
		if (pdfLink) {
			window.open(pdfLink, '_blank');
		}
		setPdfGenerationInitiated(false);
	}, [walkthrough]);

	const fetchPdfStatusFn = useMemo(
		() => fetchPdfStatus(walkthroughId),
		[fetchPdfStatus, walkthroughId]
	);

	useEffect(() => {
		getWalkthrough(walkthroughId).then((wt) => {
			const pdfLink = nullSafeGet('pdfLink', wt);
			if (!pdfLink) {
				fetchPdfStatusFn()
					.then((res) => {
						if (res && res.completed && res.pdfLink) {
							pdfLinkRef.current = res.pdfLink;
							getWalkthrough(walkthroughId);
						} else if (Array.isArray(res) && res.length > 0) {
							setPdfGenerationInitiated(true);
						}
					})
					.catch(() => {
						// no generation in progress, do nothing
					});
			}
		});
	}, []);

	const handleTabChange = useCallback(
		(key) => history.push(`/${userType}/siteSurveys/walkthrough/detail/${walkthroughId}/${key}`),
		[history, userType, walkthroughId]
	);

	const handleDelete = useCallback(() => {
		deleteWalkthrough(walkthrough).then(() => {
			history.push(
				goBackLink(
					location,
					`/${userType}/siteSurveys/detail/${nullSafeGet('siteSurveyId', walkthrough)}/details`
				)
			);
		});
	}, [deleteWalkthrough, history, location, userType, walkthrough]);

	const checkTabActive = useCallback(
		(key) => {
			const pathname = history.location.pathname;
			return pathname.indexOf(key) !== -1;
		},
		[history.location.pathname]
	);

	const downloadCSV = useCallback(() => {
		downloadWalkthroughCSV(walkthroughId);
	}, [downloadWalkthroughCSV, walkthroughId]);

	const downloadScoreCSV = useCallback(() => {
		downloadWalkthroughScoreCSV(walkthroughId);
	}, [downloadWalkthroughScoreCSV, walkthroughId]);

	const tabs = useMemo(
		() => [
			{
				key: 'details',
				name: 'Overview',
			},
		],
		[]
	);

	const exportMenu = useMemo(
		() => (
			<Menu>
				<Menu.Item onClick={downloadScoreCSV}>Export Score CSV</Menu.Item>
				<Menu.Item onClick={downloadCSV}>Export CSV</Menu.Item>
				<Menu.Item onClick={downloadPDF}>Export PDF</Menu.Item>
			</Menu>
		),
		[downloadCSV, downloadScoreCSV, downloadPDF]
	);

	const exportButton = useMemo(
		() => (
			<Dropdown overlay={exportMenu}>
				<Button style={{ marginRight: 16 }} ghost={true}>
					<div className="flex flex-row items-center">
						<span>Export</span>
						<div className="ml-2">
							<DownOutlined />
						</div>
					</div>
				</Button>
			</Dropdown>
		),
		[exportMenu]
	);

	const { backLinkTo, backLinkText } = useMemo(() => {
		const siteSurveyPath = `/${userType}/siteSurveys/detail/${nullSafeGet(
			'siteSurvey.id',
			walkthrough
		)}/details`;
		let searchString = location.search;
		if (searchString[0] === '?') {
			searchString = searchString.slice(1);
		}
		const queryParams = queryString.parse(searchString);
		if (!queryParams.backlinkParams) {
			return { backLinkTo: siteSurveyPath, backLinkText: 'Back to site survey' };
		}
		const innerParams = queryString.parse(queryParams.backlinkParams);
		const innerPath = innerParams.pathName;
		const innerText = innerParams.backLinkText;
		if (innerPath) {
			const { pathName, backLinkText: _bt, targetCollectionName, ...rest } = innerParams;
			const restQs = new URLSearchParams(rest as Record<string, string>).toString();
			return {
				backLinkTo: restQs ? `${innerPath}?${restQs}` : innerPath,
				backLinkText: innerText || 'Back',
			};
		}
		return {
			backLinkTo: `${siteSurveyPath}?${queryParams.backlinkParams}`,
			backLinkText: 'Back to site survey',
		};
	}, [location.search, userType, walkthrough]);

	return (
		<div className="walkthroughsDetailPage">
			<WalkthroughDetailListener
				socketContext={socketContext}
				onMessage={handleMessage}
				entityIds={[walkthroughId]}
			/>
			<DetailPageHeader
				headerClassName="walkthrough-detail-header"
				exactPath={`/${userType}/siteSurveys/walkthrough/detail/:id`}
				redirectPath={`/${userType}/siteSurveys/walkthrough/detail/${walkthroughId}/details`}
				backLinkText={backLinkText}
				backLinkTo={backLinkTo}
				title={walkthroughsFetching ? null : walkthrough.title}
				subtitle={
					<div className="walkthroughsDetailPage__subtitleRow">
						<div className="walkthroughsDetailPage__subtitle">
							{nullSafeGet('location.name', walkthrough)}
						</div>
					</div>
				}
				actions={[
					pdfGenerationInitiated ? (
						<OWPoll
							key="walkthrough-pdf"
							id="walkthrough-pdf"
							fetchStatusPromises={[fetchPdfStatusFn]}
							successMessage="PDF Generated Successfully!"
							successButtonText="Open PDF"
							message="Exporting Walkthrough"
							onRefresh={onOpenPdf}
							onComplete={onPdfPollComplete}
							successMessageMandatory={pdfGenerationInitiated}
							renderElementWithProps={(props) => (
								<Dropdown overlay={exportMenu} disabled={props.disabled}>
									<Button style={{ marginRight: 16 }} ghost={true}>
										<div className="flex flex-row items-center">
											<span>Export</span>
											<div className="ml-2">
												<DownOutlined />
											</div>
										</div>
									</Button>
								</Dropdown>
							)}
						/>
					) : (
						exportButton
					),
					<AccessPermissionChecker name={PERMISSION_NAMES.DELETE_WALKTHROUGHS}>
						<DeleteConfirmButton
							onConfirm={handleDelete}
							title={'Are you sure you want to delete this walkthrough?'}
						>
							<Button key={2} ghost={true}>
								Delete
							</Button>
						</DeleteConfirmButton>
					</AccessPermissionChecker>,
				]}
				tabs={tabs}
				backgroundColor={nullSafeGet('detail.config.theme.navblock', companyConfig)}
				handleTabChange={handleTabChange}
				checkTabActive={checkTabActive}
				childRoutes={childRoutes}
			/>
		</div>
	);
};

const mapStateToProps = (state) => ({
	walkthroughsFetching: state.walkthroughs.fetching,
	walkthrough: state.walkthroughs.detail,
	companyConfig: state.company_config,
	currentUser: state.session.currentUser,
	loading: state.walkthroughs.fetching,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getWalkthrough: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughsRestCrudThunksForSupplier.readOne(id)
				: walkthroughsRestCrudThunksForBuyer.readOne(id)
		),
	deleteWalkthrough: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughsRestCrudThunksForSupplier.delete(entity, 'id', null, true)
				: walkthroughsRestCrudThunksForBuyer.delete(entity, 'id', null, true)
		),
	downloadWalkthroughCSV: (id) => dispatch(downloadWalkthroughCSV(ownProps.userType)(id)),
	downloadWalkthroughScoreCSV: (id) => dispatch(downloadWalkthroughScoreCSV(ownProps.userType)(id)),
	generatePDF: (id) => dispatch(generateWalkthroughPDF(ownProps.userType)(id)),
	fetchPdfStatus: (id) => () => dispatch(getWalkthroughPDFStatus(ownProps.userType)(id)),
});

export default withRouter<any, any>(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(WalkthroughsDetailPage as any) as any)
);
